<script setup>
import { maxLength, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { MAX_NAME_LENGTH } from '../../utils/constants/validationLimits';

import FormElement from '../molecules/form/FormElement.vue';
import FormEmailLoginHint from './form/FormEmailLoginHint.vue';
import FormInput from '../atoms/form/FormInput.vue';
import FormLabel from '../atoms/form/FormLabel.vue';
import FormToolTip from '../atoms/form/FormToolTip.vue';
import FormMessage from '../atoms/form/FormMessage.vue';
import salutationTypes from '../../data/salutation-types.json';
import FormSection from './form/FormSection.vue';
import { useApplicationStore } from '../../stores/application-store';
import ComboBox from '../headless/ComboBox.vue';

const application = useApplicationStore();
const { firstname, lastname, salutation } = storeToRefs(application);

const validations = {
    firstname: {
        required,
        maxLength: maxLength(MAX_NAME_LENGTH),
    },
    lastname: {
        required,
        maxLength: maxLength(MAX_NAME_LENGTH),
    },
    salutation: {
        required,
    },
};

const v$ = useVuelidate(validations, { firstname, lastname, salutation }, { $registerAs: 'personalData' });

function onFirstNameBlur() {
    v$.value.firstname.$touch();
    application.updateCvRenderData();
}

function onLastNameBlur() {
    v$.value.lastname.$touch();
    application.updateCvRenderData();
}

/* Salutation model */
const salutationModel = computed({
    get: () => salutationTypes.find(({ value }) => salutation.value === value) || null,
    set: (newModelValue) => { salutation.value = newModelValue.value; },
});
</script>

<template>
    <FormSection
        class="o-personalData"
        data-qa="personal data"
    >
        <FormEmailLoginHint/>
        <div class="o-personalData__salutationWrap">
            <FormElement>
                <template #start>
                    <div class="flex justify-between">
                        <FormLabel>
                            Anrede *
                        </FormLabel>
                        <FormToolTip class="text-right flex-grow">
                            Warum ist das notwendig?
                            <template #description>
                                Mit einer Anrede kann dich das Unternehmen beim Erstkontakt persönlich ansprechen.
                            </template>
                        </FormToolTip>
                    </div>
                </template>
                <ComboBox
                    id="salutation"
                    v-model="salutationModel"
                    :options="salutationTypes"
                    data-qa="salutation"
                />
                <template #end>
                    <FormMessage
                        v-if="v$.salutation.$error"
                        type="error"
                        data-qa="salutation form message"
                    >
                        <template v-if="v$.salutation.required.$invalid">
                            Bitte wähle eine Anrede aus.
                        </template>
                    </FormMessage>
                </template>
            </FormElement>
        </div>
        <div class="o-personalData__nameWrap">
            <FormElement class="o-personalData__nameWrapField">
                <template #start>
                    <FormLabel ref="firstnameLabel">
                        Vorname *
                    </FormLabel>
                </template>
                <FormInput
                    v-model.trim="firstname"
                    type="text"
                    placeholder="Vorname"
                    :status="v$.firstname.$error ? 'error' : ''"
                    :show-valid-icon="!v$.firstname.$invalid"
                    data-qa="firstname"
                    data-gtm-element="AP_F: Personal Data"
                    data-gtm-element-detail="First-Name"
                    @update:model-value="v$.firstname.$touch()"
                    @blur="onFirstNameBlur"
                />
                <template #end>
                    <FormMessage
                        v-if="v$.firstname.$error"
                        type="error"
                        data-qa="firstname form message"
                    >
                        <template v-if="v$.firstname.required.$invalid">
                            Bitte fülle das Feld Vorname aus.
                        </template>
                        <template v-else-if="v$.firstname.maxLength.$invalid">
                            Bitte fülle das Feld Vorname aus, mit max. {{ MAX_NAME_LENGTH }} Zeichen.
                        </template>
                    </FormMessage>
                </template>
            </FormElement>

            <FormElement class="o-personalData__nameWrapField">
                <template #start>
                    <FormLabel>
                        Nachname *
                    </FormLabel>
                </template>
                <FormInput
                    v-model.trim="lastname"
                    type="text"
                    placeholder="Nachname"
                    :status="v$.lastname.$error ? 'error' : ''"
                    :show-valid-icon="!v$.lastname.$invalid"
                    data-qa="lastname"
                    data-gtm-element="AP_F: Personal Data"
                    data-gtm-element-detail="Last-Name"
                    @update:model-value="v$.lastname.$touch()"
                    @blur="onLastNameBlur"
                />
                <template #end>
                    <FormMessage
                        v-if="v$.lastname.$error"
                        type="error"
                        data-qa="lastname form message"
                    >
                        <template v-if="v$.lastname.required.$invalid">
                            Bitte fülle das Feld Nachname aus.
                        </template>
                        <template v-else-if="v$.lastname.maxLength.$invalid">
                            Bitte fülle das Feld Nachname aus, mit max. {{ MAX_NAME_LENGTH }} Zeichen.
                        </template>
                    </FormMessage>
                </template>
            </FormElement>
        </div>
    </FormSection>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/settings/breakpoint';

.o-personalData {
    $root: &;

    &__nameWrap {
        display: flex;
        flex-wrap: wrap;
        margin-left: -(spacing.$k-spacing--s);
    }

    &__nameWrapField {
        padding-top: spacing.$k-spacing--m;
        padding-left: spacing.$k-spacing--s;
        width: 100%;

        @media (min-width: breakpoint.$k-breakpoint--m) {
            width: 50%;
        }
    }

    &__salutationWrap {
        padding-top: spacing.$k-spacing--m;
    }
}
</style>
